import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  GraduationCap,
  Plus,
  Edit2,
  Trash2,
  CheckCircle,
  Clock,
  AlertTriangle,
  Upload,
  FileText,
  Calendar,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TRAINING_FREQUENCIES = ['Once', 'Annual', 'Bi-Annual', 'As Needed'];

export default function PersonnelCompliance({ personnelId, isAdmin = true }) {
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTraining, setShowNewTraining] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  const [newTraining, setNewTraining] = useState({
    training_name: '',
    description: '',
    frequency: 'Annual',
    due_date: ''
  });

  useEffect(() => {
    fetchTrainings();
  }, [personnelId]);

  const fetchTrainings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/personnel/${personnelId}/compliance-training`);
      setTrainings(response.data.trainings || []);
    } catch (error) {
      console.error('Failed to load compliance trainings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTraining = async () => {
    if (!newTraining.training_name) {
      toast.error('Please enter a training name');
      return;
    }
    try {
      await axios.post(`${API}/personnel/${personnelId}/compliance-training`, newTraining);
      toast.success('Training requirement added');
      setShowNewTraining(false);
      setNewTraining({ training_name: '', description: '', frequency: 'Annual', due_date: '' });
      fetchTrainings();
    } catch (error) {
      toast.error('Failed to add training');
    }
  };

  const handleUpdateTraining = async (trainingId, data) => {
    try {
      await axios.put(`${API}/personnel/${personnelId}/compliance-training/${trainingId}`, data);
      toast.success('Training updated');
      setEditingId(null);
      fetchTrainings();
    } catch (error) {
      toast.error('Failed to update training');
    }
  };

  const handleMarkComplete = async (trainingId) => {
    const today = new Date().toISOString().split('T')[0];
    await handleUpdateTraining(trainingId, {
      status: 'Completed',
      completed_date: today
    });
  };

  const handleDeleteTraining = async (trainingId) => {
    if (!window.confirm('Delete this training requirement?')) return;
    try {
      await axios.delete(`${API}/personnel/${personnelId}/compliance-training/${trainingId}`);
      toast.success('Training deleted');
      fetchTrainings();
    } catch (error) {
      toast.error('Failed to delete training');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      'Pending': { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      'In Progress': { color: 'bg-blue-100 text-blue-800', icon: Clock },
      'Completed': { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      'Overdue': { color: 'bg-red-100 text-red-800', icon: AlertTriangle }
    };
    const { color, icon: Icon } = config[status] || config['Pending'];
    return (
      <Badge className={`${color} flex items-center gap-1`}>
        <Icon size={12} />
        {status}
      </Badge>
    );
  };

  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return null;
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Organize trainings by status
  const overdueTrainings = trainings.filter(t => t.status === 'Overdue');
  const pendingTrainings = trainings.filter(t => t.status === 'Pending' || t.status === 'In Progress');
  const completedTrainings = trainings.filter(t => t.status === 'Completed');

  if (loading) {
    return <div className="p-6 text-center text-muted-foreground">Loading compliance data...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4 bg-red-50 border-red-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="text-red-600" size={20} />
              <span className="font-medium">Overdue</span>
            </div>
            <span className="text-2xl font-bold text-red-600">{overdueTrainings.length}</span>
          </div>
        </Card>

        <Card className="p-4 bg-yellow-50 border-yellow-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="text-yellow-600" size={20} />
              <span className="font-medium">Pending</span>
            </div>
            <span className="text-2xl font-bold text-yellow-600">{pendingTrainings.length}</span>
          </div>
        </Card>

        <Card className="p-4 bg-green-50 border-green-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle className="text-green-600" size={20} />
              <span className="font-medium">Completed</span>
            </div>
            <span className="text-2xl font-bold text-green-600">{completedTrainings.length}</span>
          </div>
        </Card>
      </div>

      {/* Training Requirements */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold flex items-center gap-2">
            <GraduationCap size={18} />
            Training Requirements
          </h3>
          {isAdmin && (
            <Button size="sm" onClick={() => setShowNewTraining(true)} data-testid="new-training-btn">
              <Plus size={14} className="mr-1" /> Add Training
            </Button>
          )}
        </div>

        {/* New Training Form */}
        {showNewTraining && (
          <Card className="p-4 mb-4 bg-muted/50">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="col-span-2">
                <label className="text-xs font-medium">Training Name</label>
                <Input
                  placeholder="e.g., HIPAA Compliance, CPR Certification..."
                  value={newTraining.training_name}
                  onChange={(e) => setNewTraining({ ...newTraining, training_name: e.target.value })}
                />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium">Description</label>
                <Input
                  placeholder="Training description..."
                  value={newTraining.description}
                  onChange={(e) => setNewTraining({ ...newTraining, description: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs font-medium">Frequency</label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={newTraining.frequency}
                  onChange={(e) => setNewTraining({ ...newTraining, frequency: e.target.value })}
                >
                  {TRAINING_FREQUENCIES.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium">Due Date</label>
                <Input
                  type="date"
                  value={newTraining.due_date}
                  onChange={(e) => setNewTraining({ ...newTraining, due_date: e.target.value })}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleCreateTraining}>Add Training</Button>
              <Button size="sm" variant="outline" onClick={() => setShowNewTraining(false)}>Cancel</Button>
            </div>
          </Card>
        )}

        {/* Trainings List */}
        {trainings.length === 0 ? (
          <p className="text-center text-muted-foreground py-4">No training requirements</p>
        ) : (
          <div className="space-y-3">
            {/* Overdue Section */}
            {overdueTrainings.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-red-600 mb-2 flex items-center gap-1">
                  <AlertTriangle size={14} /> Overdue
                </h4>
                {overdueTrainings.map(training => (
                  <TrainingCard
                    key={training.id}
                    training={training}
                    isAdmin={isAdmin}
                    onMarkComplete={handleMarkComplete}
                    onUpdate={handleUpdateTraining}
                    onDelete={handleDeleteTraining}
                    getDaysUntilDue={getDaysUntilDue}
                    getStatusBadge={getStatusBadge}
                  />
                ))}
              </div>
            )}

            {/* Pending Section */}
            {pendingTrainings.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-yellow-600 mb-2 flex items-center gap-1">
                  <Clock size={14} /> Pending / In Progress
                </h4>
                {pendingTrainings.map(training => (
                  <TrainingCard
                    key={training.id}
                    training={training}
                    isAdmin={isAdmin}
                    onMarkComplete={handleMarkComplete}
                    onUpdate={handleUpdateTraining}
                    onDelete={handleDeleteTraining}
                    getDaysUntilDue={getDaysUntilDue}
                    getStatusBadge={getStatusBadge}
                  />
                ))}
              </div>
            )}

            {/* Completed Section */}
            {completedTrainings.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-green-600 mb-2 flex items-center gap-1">
                  <CheckCircle size={14} /> Completed
                </h4>
                {completedTrainings.map(training => (
                  <TrainingCard
                    key={training.id}
                    training={training}
                    isAdmin={isAdmin}
                    onMarkComplete={handleMarkComplete}
                    onUpdate={handleUpdateTraining}
                    onDelete={handleDeleteTraining}
                    getDaysUntilDue={getDaysUntilDue}
                    getStatusBadge={getStatusBadge}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}

// Training Card Component
function TrainingCard({ training, isAdmin, onMarkComplete, onUpdate, onDelete, getDaysUntilDue, getStatusBadge }) {
  const daysUntilDue = getDaysUntilDue(training.due_date);
  
  return (
    <div className="p-3 border rounded-lg mb-2">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium">{training.training_name}</span>
            {getStatusBadge(training.status)}
            <Badge variant="outline" className="text-xs">{training.frequency}</Badge>
          </div>
          {training.description && (
            <p className="text-sm text-muted-foreground">{training.description}</p>
          )}
          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
            {training.due_date && (
              <span className="flex items-center gap-1">
                <Calendar size={12} />
                Due: {training.due_date}
                {daysUntilDue !== null && training.status !== 'Completed' && (
                  <span className={daysUntilDue < 0 ? 'text-red-600 font-medium' : daysUntilDue < 14 ? 'text-yellow-600' : ''}>
                    ({daysUntilDue < 0 ? `${Math.abs(daysUntilDue)} days overdue` : `${daysUntilDue} days left`})
                  </span>
                )}
              </span>
            )}
            {training.completed_date && (
              <span className="flex items-center gap-1 text-green-600">
                <CheckCircle size={12} />
                Completed: {training.completed_date}
              </span>
            )}
            {training.certificate_file && (
              <span className="flex items-center gap-1">
                <FileText size={12} />
                Certificate uploaded
              </span>
            )}
          </div>
        </div>
        {isAdmin && (
          <div className="flex gap-1 ml-4">
            {training.status !== 'Completed' && (
              <Button
                size="sm"
                variant="outline"
                className="text-green-600 h-8"
                onClick={() => onMarkComplete(training.id)}
              >
                <CheckCircle size={14} className="mr-1" /> Complete
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              className="text-red-600 h-8 w-8 p-0"
              onClick={() => onDelete(training.id)}
            >
              <Trash2 size={14} />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
